import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, PartyPopper, Plus, X } from "lucide-react";
import { api, LIBRARIES } from "@/lib/api";

const AGES = ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const fmtDateOpt = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
};

const initial = {
  parent_name: "", email: "", phone: "", children: [{ name: "", age: "" }],
  emergency_name: "", emergency_phone: "", library_location: "", camp_date: "",
  notes: "", permission: false, terms: false,
};

export const EnrollForm = ({ events = [] }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const setChild = (idx, key, val) => {
    setForm((f) => ({
      ...f,
      children: f.children.map((c, i) => (i === idx ? { ...c, [key]: val } : c)),
    }));
    setErrors((e) => ({ ...e, children: undefined }));
  };
  const addChild = () => setForm((f) => ({ ...f, children: [...f.children, { name: "", age: "" }] }));
  const removeChild = (idx) =>
    setForm((f) => ({ ...f, children: f.children.filter((_, i) => i !== idx) }));

  const validate = () => {
    const e = {};
    if (!form.parent_name.trim()) e.parent_name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Required";
    if (form.children.some((c) => !c.name.trim() || !c.age)) e.children = "Enter each child's name and age";
    if (!form.library_location) e.library_location = "Required";
    if (!form.camp_date) e.camp_date = "Required";
    if (!form.permission) e.permission = "Please confirm";
    if (!form.terms) e.terms = "Please confirm";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enrollments", form);
      setDone(true);
      toast.success("Enrollment submitted! 🌟");
      setForm(initial);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (k) =>
    `w-full rounded-xl bg-white border-2 px-4 py-3 font-medium text-emerald-900 placeholder-emerald-400 focus:outline-none focus:ring-2 focus:ring-pink-400 transition ${
      errors[k] ? "border-red-400" : "border-teal-200 focus:border-pink-400"
    }`;
  const labelCls = "block font-display font-semibold text-white mb-1.5 text-sm";

  if (done) {
    return (
      <div data-testid="enroll-success" className="bg-white rounded-[2rem] p-10 text-center shadow-pop">
        <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-12 h-12 text-white" />
        </div>
        <h3 className="font-display font-bold text-3xl text-pink-500 flex items-center justify-center gap-2">
          <PartyPopper className="w-7 h-7" /> You're all set!
        </h3>
        <p className="mt-4 text-emerald-800 font-medium text-lg">
          Thank you for enrolling with WonderStarzLV! We've received your registration and our
          team will reach out soon to confirm your child's spot.
        </p>
        <button
          data-testid="enroll-another-btn"
          onClick={() => setDone(false)}
          className="mt-7 bg-teal-400 text-white font-display font-bold px-8 py-3 rounded-full hover:bg-teal-500 transition"
        >
          Enroll Another Child
        </button>
      </div>
    );
  }

  return (
    <form data-testid="enroll-form" onSubmit={onSubmit} noValidate className="bg-pink-500 rounded-[2rem] p-7 sm:p-9 shadow-pop">
      <h3 className="font-display font-bold text-3xl text-white">Enroll Your Child Today!</h3>
      <p className="text-pink-100 font-medium mt-1 mb-6">Spots fill up fast — secure your child's spot now!</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Parent/Guardian Name *</label>
          <input data-testid="input-parent-name" className={inputCls("parent_name")} value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} placeholder="Jane Doe" />
        </div>
        <div>
          <label className={labelCls}>Email Address *</label>
          <input data-testid="input-email" type="email" className={inputCls("email")} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" />
        </div>
        <div>
          <label className={labelCls}>Phone Number *</label>
          <input data-testid="input-phone" className={inputCls("phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(702) 555-0100" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Children * <span className="font-normal text-pink-100">(add as many as you need)</span></label>
          <div className="space-y-3">
            {form.children.map((child, idx) => (
              <div key={idx} data-testid={`child-row-${idx}`} className="flex gap-2 sm:gap-3 items-start">
                <input
                  data-testid={`input-child-name-${idx}`}
                  className={`${inputCls("children")} flex-1`}
                  value={child.name}
                  onChange={(e) => setChild(idx, "name", e.target.value)}
                  placeholder={`Child ${idx + 1} name`}
                />
                <select
                  data-testid={`select-child-age-${idx}`}
                  className={`${inputCls("children")} w-24 sm:w-32 shrink-0`}
                  value={child.age}
                  onChange={(e) => setChild(idx, "age", e.target.value)}
                >
                  <option value="">Age</option>
                  {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {form.children.length > 1 && (
                  <button
                    type="button"
                    data-testid={`remove-child-${idx}`}
                    onClick={() => removeChild(idx)}
                    aria-label="Remove child"
                    className="mt-2.5 text-pink-100 hover:text-white shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {errors.children && <p className="text-yellow-200 text-sm font-semibold mt-1.5">{errors.children}</p>}
          <button
            type="button"
            data-testid="add-child-btn"
            onClick={addChild}
            className="mt-3 inline-flex items-center gap-1 bg-yellow-400 text-emerald-900 font-display font-bold text-sm px-4 py-2 rounded-full hover:bg-yellow-300 transition"
          >
            <Plus className="w-4 h-4" /> Add another child
          </button>
        </div>
        <div>
          <label className={labelCls}>Emergency Contact Name</label>
          <input data-testid="input-emergency-name" className={inputCls("emergency_name")} value={form.emergency_name} onChange={(e) => set("emergency_name", e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <label className={labelCls}>Emergency Contact Phone</label>
          <input data-testid="input-emergency-phone" className={inputCls("emergency_phone")} value={form.emergency_phone} onChange={(e) => set("emergency_phone", e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <label className={labelCls}>Select Library Location *</label>
          <select data-testid="select-library" className={inputCls("library_location")} value={form.library_location} onChange={(e) => set("library_location", e.target.value)}>
            <option value="">Choose a location</option>
            {LIBRARIES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Select Camp Date *</label>
          <select
            data-testid="select-camp-date"
            className={inputCls("camp_date")}
            value={form.camp_date}
            onChange={(e) => {
              const val = e.target.value;
              set("camp_date", val);
              const ev = events.find((x) => x.date === val);
              if (ev) set("library_location", ev.library_name);
            }}
          >
            <option value="">Choose a date</option>
            {events.map((ev) => (
              <option key={ev.id || ev.date} value={ev.date}>
                {fmtDateOpt(ev.date)} — {ev.library_name}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Allergies / Medical Notes</label>
          <textarea data-testid="input-notes" rows={3} className={inputCls("notes")} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Let us know about allergies, medications, or anything we should know." />
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input data-testid="check-permission" type="checkbox" checked={form.permission} onChange={(e) => set("permission", e.target.checked)} className="mt-1 w-5 h-5 accent-yellow-400 shrink-0" />
          <span className={`text-sm font-medium ${errors.permission ? "text-yellow-200" : "text-white"}`}>
            I give permission for my child to participate in WonderStarzLV camp activities. *
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input data-testid="check-terms" type="checkbox" checked={form.terms} onChange={(e) => set("terms", e.target.checked)} className="mt-1 w-5 h-5 accent-yellow-400 shrink-0" />
          <span className={`text-sm font-medium ${errors.terms ? "text-yellow-200" : "text-white"}`}>
            I agree to the program policies, safety rules, liability waiver, and pickup procedures. *
          </span>
        </label>
      </div>

      <button
        data-testid="submit-enrollment-btn"
        type="submit"
        disabled={submitting}
        className="mt-7 w-full bg-yellow-400 text-emerald-900 font-display font-bold text-xl py-4 rounded-full hover:bg-yellow-300 hover:scale-[1.02] transition-all disabled:opacity-60 disabled:hover:scale-100"
      >
        {submitting ? "Submitting..." : "Submit Enrollment"}
      </button>
    </form>
  );
};
